import { zodResolver } from '@hookform/resolvers/zod';
import { ItemSchemaCurrencyEnum } from '@nest/domain-entities-metadata-generated';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	Input,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TextNumber,
	useValidationResultDialog
} from '@nest/uikit-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitScanInput } from '../components/HandlingUnitScanInput';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { StockItemScanInput } from '../components/StockItemScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

type TabNames = 'stockItem' | 'batch' | 'quantity' | 'destinationHandlingUnit' | 'destinationSourceLocation';

export default function InboundDeliveryOrderProcessItemPage() {
	const client = useTerminalClient();
	const queryClient = useQueryClient();
	const originCountryRef = useRef<HTMLInputElement>(null);

	const formSchema = useMemo(
		() =>
			z.object({
				stockItem: z.any().nullable(),
				destinationLocation: z.any().nullable(),
				destinationHandlingUnit: z.any().nullable(),
				batch: z
					.string()
					.refine(
						val => {
							if (val === '') return true; // povoluje prázdný string
							const num = parseInt(val, 10);
							return !isNaN(num) && num >= 2010 && num <= 2030;
						},
						{
							message: 'Šarže musí být číslo mezi 2010 a 2030 nebo prázdné'
						}
					)
					.optional(),
				originCountry: z.preprocess(val => (val === '' ? undefined : val), ItemSchemaCurrencyEnum.optional()),
				quantity: z.number().min(0, 'Množství musí být větší než 0').default(0)
			}),
		[queryClient]
	);

	type FormData = z.infer<typeof formSchema>;

	const inboundDeliveryOrderLineId = useNumberParam('lineId');
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<TabNames>('stockItem');

	// Initialize form with react-hook-form
	const form = useForm<FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			stockItem: null,
			destinationLocation: null,
			destinationHandlingUnit: null,
			batch: '',
			originCountry: undefined,
			quantity: 0
		},
		mode: 'onSubmit'
	});

	const batch = useWatch({ control: form.control, name: 'batch' });

	useEffect(() => {
		form.trigger('batch');
	}, [batch]);

	const query = useQuery({
		queryKey: ['inbound-delivery-order-line', inboundDeliveryOrderLineId],
		queryFn: () =>
			client.inboundDeliveryOrderProcess.getInboundDeliveryOrderLineById.execute({
				id: inboundDeliveryOrderLineId!,
				populate: ['stockItem', 'stockItem.item']
			}),
		enabled: !!inboundDeliveryOrderLineId
	});

	const receiveMutation = useMutation({
		mutationFn: (data: FormData) =>
			client.inboundDeliveryOrderProcess.receiveInboundDeliveryOrderMutation.execute({
				inboundDeliveryOrderLineId: inboundDeliveryOrderLineId!,
				destinationLocationId: data.destinationLocation?.id,
				quantity: data.quantity,
				stockItemId: data.stockItem?.id,
				batch: data.batch,
				destinationHandlingUnitId: data.destinationHandlingUnit?.id,
				originCountry: data.originCountry
			}),
		onSuccess: () => {
			// Navigate back to process page
			const urlParts = window.location.pathname.split('/');
			const inboundDeliveryOrderId = urlParts[urlParts.length - 3];
			navigate(`/inbound/delivery/orders/${inboundDeliveryOrderId}/process`);
		}
	});

	useValidationResultDialog({
		errors: [query.error, receiveMutation.error],
		control: form.control
	});

	const inboundDeliveryOrderLine = query.data;

	const stockItemColorState = () => {
		const stockItem = form.watch('stockItem');
		const suggestedStockItem = inboundDeliveryOrderLine?.stockItem;

		if (stockItem == null) {
			return 'bg-transparent';
		} else if (stockItem.id == suggestedStockItem?.id) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		} else {
			return 'bg-red-200';
		}
	};

	const activateNextTab = () => {
		const tabNames: TabNames[] = ['stockItem', 'batch', 'quantity', 'destinationHandlingUnit', 'destinationSourceLocation'];
		let activeIndex = tabNames.indexOf(activeTab);
		let nextIndex = (activeIndex + 1) % tabNames.length;
		setActiveTab(tabNames[nextIndex]);
	};

	const onHandlingUnitChange = (value: any) => {
		if (value) {
			form.setValue('destinationHandlingUnit', value);
			activateNextTab();
		}
	};

	const onSourceLocationChange = (value: any) => {
		if (value) {
			form.setValue('destinationLocation', value);
			activateNextTab();
		}
	};

	const onStockItemChange = (value: any) => {
		form.setValue('stockItem', value);

		if (value) {
			if (inboundDeliveryOrderLine?.stockItem?.item?.productionBatchEvidence) {
				setActiveTab('batch');
			} else {
				setActiveTab('quantity');
			}
		}
	};

	const handleConfirm = (data: FormData) => {
		receiveMutation.mutate(data);
	};

	const handleBack = () => {
		// Navigate back to process page
		const urlParts = window.location.pathname.split('/');
		const inboundDeliveryOrderId = urlParts[urlParts.length - 3];
		navigate(`/inbound/delivery/orders/${inboundDeliveryOrderId}/process`);
	};

	const handleBatchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			originCountryRef.current?.focus();
		}
	};

	return (
		<TerminalScreen showBackButton topBar={inboundDeliveryOrderLine?.stockItem?.id && <StockItemLinkDetail stockItemId={inboundDeliveryOrderLine?.stockItem?.id} />}>
			<div className="h-full flex flex-col">
				<div className="flex-1">
					<div className="p-4">
						<div className="mb-4">
							<div>Zbývá: {inboundDeliveryOrderLine?.quantityRemaining}</div>
						</div>
					</div>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(handleConfirm)} className="h-full flex flex-col">
							<Tabs value={activeTab} onValueChange={value => setActiveTab(value as TabNames)}>
								<TabsList className="mt-auto">
									<TabsTrigger value="stockItem" className={`${stockItemColorState()}`}>
										SKU
									</TabsTrigger>
									{inboundDeliveryOrderLine?.stockItem?.item?.productionBatchEvidence && <TabsTrigger value="batch">Šarže</TabsTrigger>}
									<TabsTrigger value="quantity">Množství</TabsTrigger>
									<TabsTrigger value="destinationHandlingUnit">Klec</TabsTrigger>
									<TabsTrigger value="destinationSourceLocation">Lokace</TabsTrigger>
								</TabsList>

								<div className="flex-1 overflow-auto">
									<TabsContent value="stockItem" className={`h-full p-4 ${stockItemColorState()}`}>
										<FormField
											control={form.control}
											name="stockItem"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<StockItemScanInput value={field.value} autoFocus={true} placeholder="Naskenujte položku..." onChange={onStockItemChange} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</TabsContent>

									{inboundDeliveryOrderLine?.stockItem?.item?.productionBatchEvidence && (
										<TabsContent value="batch" className="h-full p-4">
											<div className="flex flex-col gap-2">
												<FormField
													control={form.control}
													name="batch"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input {...field} placeholder="Šarže" autoComplete="off" autoFocus={true} onKeyDown={handleBatchKeyDown} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="originCountry"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input {...field} placeholder="Země původu" ref={originCountryRef} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</TabsContent>
									)}

									<TabsContent value="quantity" className="h-full p-4">
										<FormField
											control={form.control}
											name="quantity"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<TextNumber
															value={typeof field.value === 'number' ? field.value : 0}
															onChange={field.onChange}
															placeholder="Množství"
															autoFocus={true}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</TabsContent>

									<TabsContent value="destinationHandlingUnit" className="h-full p-4">
										<FormField
											control={form.control}
											name="destinationHandlingUnit"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<HandlingUnitScanInput autoFocus={true} placeholder="Naskenujte klec..." onChange={onHandlingUnitChange} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</TabsContent>

									<TabsContent value="destinationSourceLocation" className="h-full p-4">
										<FormField
											control={form.control}
											name="destinationLocation"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<StorageLocationScanInput autoFocus={true} placeholder="Naskenujte lokaci..." onChange={onSourceLocationChange} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</TabsContent>
								</div>
							</Tabs>
							<Button variant="default" type="submit" disabled={receiveMutation.isPending}>
								{receiveMutation.isPending ? 'Zpracovávám...' : 'Potvrdit položku'}
							</Button>
						</form>
					</Form>
				</div>
			</div>
		</TerminalScreen>
	);
}
